import React from 'react';

const TermsOfService = () => {
  const companyName = "Coooly Ltd";
  const companyEmail = "support@coooly.co.uk";
  const effectiveDate = "April 25, 2025"; // Date from the screenshot

  return (
    <div>
      <p className="lead !mb-6 !text-base">Effective Date: {effectiveDate}</p>

      <h2>1. Agreement to Terms</h2>
      <p>
        These Terms of Service ("Terms") constitute a legally binding agreement made between you, a self-employed dental professional based in the United Kingdom ("you", "User"), and {companyName} ("Company", "we", "us", or "our"), concerning your access to and use of our web application and related services (collectively, the "Service") designed to assist you in managing your invoicing and related administrative tasks. **The Service is intended for use strictly within the United Kingdom.**
      </p>

      <p>
        By accessing or using the Service, you agree to be bound by these Terms. If you disagree with any part of the Terms, you may not access the Service.
      </p>

      <h2>2. Subscription Terms</h2>
      <p>
        The Service is billed on a subscription basis. You will be billed in advance on a recurring basis according to the pricing plan you select for your individual use.
      </p>

      <h3>2.1 Free Trial</h3>
      <p>
        We may offer a free trial period for our subscription plans. At the end of the free trial period, you will be automatically billed for the subscription plan you selected unless you cancel your subscription before the end of the trial period.
      </p>

      <h3>2.2 Billing Cycle</h3>
      <p>
        All plans are billed annually and payment will be charged to the payment method you provide at the time of purchase. Your subscription will automatically renew unless it is canceled at least 24 hours before the end of the current billing period.
      </p>

      <h3>2.3 Cancellation and Refunds</h3>
      <p>
        You may cancel your subscription at any time by contacting us at {companyEmail} or using the cancellation feature within your account settings. If you cancel your subscription, you will continue to have access to the Service until the end of your current billing period. We do not provide refunds for partial subscription periods or unused time after cancellation.
      </p>

      <p>
        In exceptional circumstances, at our sole discretion, refunds may be considered. Please contact {companyEmail} with any refund requests.
      </p>

      <h2>3. User Accounts</h2>
      <p>
        When you create an account with us, you must provide accurate, complete, and current information about yourself and your professional practice. Failure to do so constitutes a breach of these Terms, which may result in immediate termination of your account.
      </p>

      <p>
        You are responsible for safeguarding the password and login credentials that you use to access the Service and for any activities or actions under your account. You agree not to disclose your password or login credentials to any third party. You must notify us immediately upon becoming aware of any breach of security or unauthorized use of your account.
      </p>

      <h2>4. Acceptable Use</h2>
      <p>
        You agree not to use the Service:
      </p>
      <ul>
        <li>In any way that violates any applicable national (UK) or international law or regulation.</li>
        <li>To impersonate or attempt to impersonate the Company, a Company employee, another user, or any other person or entity.</li>
        <li>To engage in any other conduct that restricts or inhibits anyone's use or enjoyment of the Service, or which, as determined by us, may harm the Company or users of the Service or expose them to liability.</li>
        <li>To attempt to access any parts of the Service that you are not authorized to access.</li>
        <li>To use the Service in any manner that could disable, overburden, damage, or impair it or interfere with any other party's use of the Service.</li>
      </ul>

      <h2>5. Intellectual Property</h2>
      <p>
        The Service and its original content (excluding User Data as defined below), features, and functionality are and will remain the exclusive property of {companyName} and its licensors. The Service is protected by copyright, trademark, and other laws of both the United Kingdom and foreign countries. Our trademarks and trade dress may not be used in connection with any product or service without the prior written consent of {companyName}.
      </p>

      <h3>5.1 User Content and Data</h3>
      <p>
        You retain all rights to the data you input, upload, submit, or create through the Service related to your professional activities, such as your client (practice) details, service descriptions, rates, invoice information, and expense records ("User Data"). By providing User Data through the Service, you grant us a worldwide, non-exclusive, royalty-free license to use, reproduce, modify, perform, display, and distribute such User Data solely for the purpose of providing and improving the Service to you. This includes generating your invoices, reports, and performing calculations as directed by you within the Service.
      </p>

      <p>
        You represent and warrant that your User Data is accurate, that you have all necessary rights, permissions, and consents to provide the User Data to us for processing within the Service, and that its use within the Service as contemplated by these Terms does not violate any third-party intellectual property rights or privacy rights.
      </p>

      <h2>6. Limitations of Liability</h2>
      <p>
        To the maximum extent permitted by law, in no event shall the Company, its directors, employees, partners, agents, suppliers, or affiliates be liable for any indirect, incidental, special, consequential, or punitive damages, including without limitation, loss of profits, data, use, goodwill, or other intangible losses, resulting from:
      </p>
      <ul>
        <li>Your access to or use of or inability to access or use the Service.</li>
        <li>Any conduct or content of any third party on the Service.</li>
        <li>Any content obtained from the Service.</li>
        <li>Unauthorized access, use, or alteration of your transmissions or content.</li>
        <li>Any errors or omissions in the Service's operation.</li>
      </ul>
      <p>
        In any case, our total liability under these Terms shall be limited to the amount you paid us, if any, for use of the Service during the twelve (12) months preceding the event giving rise to the liability.
      </p>

      <h2>7. Disclaimers</h2>
      <p>
        The Service is provided on an "AS IS" and "AS AVAILABLE" basis. The Company makes no warranties, expressed or implied, regarding the operation of the Service, or the information, content or materials included therein. To the fullest extent permissible by applicable law, the Company disclaims all warranties, expressed or implied, including, but not limited to, implied warranties of merchantability and fitness for any particular purpose.
      </p>

      <p>
        While we strive for accuracy, the Company specifically disclaims any liability for errors in calculations made by the Service, including but not limited to invoice totals, tax estimations, and financial reports. You, as a self-employed professional, are solely responsible for verifying all calculations, ensuring their accuracy for your own accounting, tax compliance, and professional record-keeping purposes. The Service is a tool to assist you, not a substitute for professional financial or tax advice.
      </p>

      <h2>8. Data Security and Backup</h2>
      <p>
        We implement security measures designed to protect your data. However, no method of transmission or electronic storage is 100% secure. Therefore, while we strive to use commercially acceptable means to protect your personal information, we cannot guarantee its absolute security.
      </p>

      <p>
        You are responsible for maintaining your own backup of any critical User Data you input into the Service. While we make reasonable efforts to ensure data integrity and availability, you should not rely solely on the Service for the primary storage of your essential business records. We recommend regularly exporting your data where such features are available.
      </p>

      <h3>8.1 Receipt Upload and Storage</h3>
      <p>
        If you choose to upload receipt images or documents through our expense tracking features, these files will be compressed and stored securely in our cloud storage system. You are responsible for ensuring that any uploaded receipts contain only appropriate business-related information and do not violate any third-party rights.
      </p>

      <h3>8.2 Data Retention and Automatic Cleanup</h3>
      <p>
        In accordance with UK tax record retention requirements, we will retain your expense records, receipts, and related business data for up to seven (7) years from the date of creation. After this period, we may automatically delete old records to optimize storage and maintain system performance.
      </p>
      
      <p>
        Before any automatic deletion occurs, we will provide you with at least sixty (60) days advance notice via email, giving you the opportunity to download and backup any records you wish to preserve. Once deleted, this data cannot be recovered, and we strongly recommend maintaining your own backup copies of important business records.
      </p>

      <p>
        You may request earlier deletion of specific records by contacting us at {companyEmail}, subject to any legal or regulatory retention requirements.
      </p>

      <h2>8.5 Job Search Features</h2>
      <p>
        Our job search functionality aggregates publicly available job listings from various sources to help you find relevant opportunities. We do not guarantee the accuracy, completeness, or availability of any job listings. You are responsible for verifying all job details directly with the employer before applying or accepting any position.
      </p>

      <p>
        When using location-based job search features, we may store your approximate location preferences to improve search results. This location data is used solely for providing relevant job matches and is not shared with third parties.
      </p>

      <h2>9. Indemnification</h2>
      <p>
        You agree to defend, indemnify, and hold harmless the Company, its officers, directors, employees, and agents, from and against any claims, liabilities, damages, judgments, awards, losses, costs, expenses, or fees (including reasonable attorneys' fees) arising out of or relating to your violation of these Terms or your use of the Service, including, but not limited to, your User Data, any use of the Service's content, services, and products other than as expressly authorized in these Terms, or your professional activities.
      </p>

      <h2>10. Governing Law</h2>
      <p>
        These Terms shall be governed and construed in accordance with the laws of England and Wales. Our failure to enforce any right or provision of these Terms will not be considered a waiver of those rights. As this Service is intended for users within the United Kingdom, any disputes will be subject to the exclusive jurisdiction of the courts of England and Wales.
      </p>

      <h2>11. Dispute Resolution</h2>
      <p>
        Any legal action or proceeding relating to your access to, or use of, the Service or these Terms shall be instituted in the courts of England and Wales. You agree to submit to the jurisdiction of the courts of England and Wales and agree that venue in these courts is proper in any such legal action or proceeding.
      </p>

      <h2>12. Service Downtime and Maintenance</h2>
      <p>
        We reserve the right to perform scheduled maintenance on the Service, which may result in temporary service unavailability. We will make reasonable efforts to notify you of such scheduled maintenance in advance. We are not liable for any service interruptions or data loss that may occur during such maintenance periods.
      </p>

      <h2>13. Termination</h2>
      <p>
        We may terminate or suspend your account immediately, without prior notice or liability, for any reason whatsoever, including without limitation if you breach these Terms. Upon termination, your right to use the Service will immediately cease.
      </p>

      <h2>14. Changes to Terms</h2>
      <p>
        We reserve the right, at our sole discretion, to modify or replace these Terms at any time. If a revision is material, we will try to provide at least 30 days' notice prior to any new terms taking effect. What constitutes a material change will be determined at our sole discretion.
      </p>

      <p>
        By continuing to access or use our Service after those revisions become effective, you agree to be bound by the revised terms. If you do not agree to the new terms, please stop using the Service.
      </p>

      <h2>15. Contact Us</h2>
      <p>
        If you have any questions about these Terms, please contact us at:
      </p>
      <p>{companyName}</p>
      <p>Email: {companyEmail}</p>
    </div>
  );
};

export default TermsOfService;